import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import { createOtp, verifyOtp } from '../../store/reducers/authReducer';
import './ForgotPassword.css';
import logo from '../../assets/logo-large.png';

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '']);
  const [showOTPInput, setShowOTPInput] = useState(false);
  const [isEmailSubmitted, setIsEmailSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { isLoading } = useSelector((state: RootState) => state.auth);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await dispatch(createOtp({ email, type: 'UFP' })).unwrap();
      setIsEmailSubmitted(true);
      setShowOTPInput(true);
    } catch (err: any) {
      console.error('OTP generation failed:', err);
      setError(err.message || 'Failed to send OTP. Please try again.');
    }
  };

  const handleOTPChange = (index: number, value: string) => {
    if (value.length > 1) return;
    const newOTP = [...otp];
    newOTP[index] = value;
    setOtp(newOTP);
    if (value && index < 3) {
      const nextInput = document.querySelector(`input[name=otp-${index + 1}]`) as HTMLInputElement;
      nextInput?.focus();
    }
  };

  const handleOTPKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.querySelector(`input[name=otp-${index - 1}]`) as HTMLInputElement;
      prevInput?.focus();
    }
  };

  const handleOTPVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const otpValue = otp.join('');

    if (otpValue.length !== 4) {
      setError('Please enter a 4-digit OTP.');
      return;
    }

    try {
      const { token } = await dispatch(verifyOtp({ email, type: 'UFP', otp: Number(otpValue) })).unwrap();
      navigate(`/reset-password?email=${email}&token=${token}`);
    } catch (err: any) {
      console.error('OTP verification failed:', err);
      setError(err.message || 'OTP verification failed. Please try again.');
    }
  };

  return (
    <div className="forgot-password-container">
      <motion.div 
        className="forgot-password-image"
        initial={{ x: 300, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: -300, opacity: 0 }}
        transition={{ type: "spring", stiffness: 200, damping: 30 }}
      />
      <motion.div 
        className="forgot-password-form-side"
        initial={{ x: -300, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: 300, opacity: 0 }}
        transition={{ type: "spring", stiffness: 200, damping: 30 }}
      >
        <div className="forgot-password-card">
          <motion.div 
            className="logo-container"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <img src={logo} alt="Logo" className="forgot-password-logo" />
          </motion.div>
          <div className="forgot-password-form-container">
            <AnimatePresence mode="wait">
              {!showOTPInput && !isEmailSubmitted ? (
                <motion.div
                  key="email-form"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  <motion.h2 
                    className="card-heading"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                    Forgot Password
                  </motion.h2>
                  <motion.div 
                    className="tlborder"
                    initial={{ opacity: 0, scaleX: 0 }}
                    animate={{ opacity: 1, scaleX: 1 }}
                    transition={{ delay: 0.4 }}
                  />
                    <motion.p 
                    className="forgot-password-description"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    >
                    Enter your email address and we'll send you a verification code to reset your password.
                    </motion.p>
                  <form onSubmit={handleEmailSubmit}>
                    <motion.div 
                      className="form-group"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.6 }}
                    >
                      <input
                        type="email"
                        className="form-control"
                        placeholder="Email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </motion.div>
                    {error && <motion.div className="error-message">{error}</motion.div>}
                    <motion.button 
                      type="submit" 
                      className="btn btn-primary"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.7 }}
                      whileHover={{ y: -2 }}
                      whileTap={{ y: 0 }}
                      disabled={isLoading}
                    >
                      {isLoading ? 'Sending...' : 'Submit'}
                    </motion.button>
                  </form>
                </motion.div>
              ) : showOTPInput ? (
                <motion.div
                  key="otp-form"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  <motion.h2 
                    className="card-heading"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                    Verify Your Email
                  </motion.h2>
                  <motion.div 
                    className="tlborder"
                    initial={{ opacity: 0, scaleX: 0 }}
                    animate={{ opacity: 1, scaleX: 1 }}
                    transition={{ delay: 0.4 }}
                  />
                  <motion.p 
                    className="forgot-password-description"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                  >
                    We've sent a verification code to {email}. Please enter it below.
                  </motion.p>
                  <form onSubmit={handleOTPVerification}>
                    <motion.div 
                      className="otp-inputs"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.6 }}
                    >
                      {otp.map((digit, index) => (
                        <input
                          key={index}
                          type="text"
                          name={`otp-${index}`}
                          value={digit}
                          onChange={(e) => handleOTPChange(index, e.target.value)}
                          onKeyDown={(e) => handleOTPKeyDown(index, e)}
                          maxLength={1}
                          className="otp-input"
                        />
                      ))}
                    </motion.div>
                    {error && <motion.div className="error-message">{error}</motion.div>}
                    <motion.button 
                      type="submit" 
                      className="btn btn-primary"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.7 }}
                      whileHover={{ y: -2 }}
                      whileTap={{ y: 0 }}
                      disabled={isLoading}
                    >
                      {isLoading ? 'Verifying...' : 'Verify OTP'}
                    </motion.button>
                    <button
                      type="button"
                      className="resend-link"
                      onClick={handleEmailSubmit} 
                      disabled={isLoading}
                    >
                      Didn't receive the code? Resend
                    </button>
                  </form>
                </motion.div>
              ) : (
                <motion.div 
                  key="success"
                  className="success-message"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  <motion.h2 
                    className="card-heading"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                    Check Your Email
                  </motion.h2>
                  <motion.div 
                    className="tlborder"
                    initial={{ opacity: 0, scaleX: 0 }}
                    animate={{ opacity: 1, scaleX: 1 }}
                    transition={{ delay: 0.4 }}
                  />
                  <motion.p 
                    className="forgot-password-description"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                  >
                    If an account exists with {email}, you will receive password reset instructions.
                  </motion.p>
                </motion.div>
              )}
            </AnimatePresence>
            <motion.div 
              className="mt-3 text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
            >
              <Link to="/login" className="back-to-login">
                Back to Login
              </Link>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ForgotPassword; 